import React, { useState } from "react";
import PageHero from "@/components/PageHero";
import Section from "@/components/Section";
import NaturalNextSteps from "@/components/NaturalNextSteps";
import InterestModal from "@/components/InterestModal";
import TopImage from "@/components/TopImage";
import BodyText from "@/components/BodyText";
import useContent from "@/hooks/useContent";
import useSeo from "@/hooks/useSeo";
import { IMG } from "@/lib/images";

export default function WeeklyEdge() {
  const [open, setOpen] = useState(false);
  const t = useContent("weekly_edge");
  useSeo("weekly_edge", {
    title: "Weekly Quantum Edge — Quantum Results",
    description:
      "A weekly opportunity to reconnect, reflect, and grow alongside a thoughtful community.",
  });
  return (
    <div data-testid="page-weekly-edge">
      <TopImage src={IMG.weeklyEdge.hero.src} alt={IMG.weeklyEdge.hero.alt} credit={IMG.weeklyEdge.hero.credit} testId="weekly-edge-top-image" />
      <PageHero
        eyebrow={t("hero_eyebrow", "Weekly Quantum Edge")}
        title={t("hero_title", <>Real change isn't created in a single <span className="text-brand-blue">moment</span>.</>)}
        lede={t("hero_lede", "A simple rhythm of returning to you. A place to reconnect with yourself, strengthen your thinking, and continue becoming the person you're intentionally creating.")}
        testId="wqe-hero"
      />

      <Section testId="wqe-ongoing">
        <BodyText
          className=""
          value={t.body(
            "ongoing_body",
            `Many of us have experienced moments that changed the way we think.

For a while, everything feels different. Then life becomes busy again. Responsibilities return. Old habits quietly reappear.

Not because we failed. Because life has a way of pulling our attention back toward what's immediate.

Weekly Quantum Edge was created to provide a simple rhythm of returning to you.`
          )}
        />
      </Section>

      <section className="bg-brand-paper/60 border-y border-brand-rule/60">
        <Section eyebrow={t("section2_eyebrow", "Recognition")} title={t("section2_title", "You may recognize this pattern…")} testId="wqe-recognition">
          <BodyText
            className=""
            value={t.body(
              "section2_body",
              `Have you ever noticed how easy it is to lose sight of what matters most?

Not all at once. Just a little at a time.

Life becomes busy. Responsibilities demand your attention. Important conversations are replaced by urgent tasks. Before long, the clarity you once felt begins to fade.

It doesn't mean you've stopped growing. It simply means you're living a real life.

**Weekly Quantum Edge was created for those moments. A simple opportunity to pause, reconnect, and continue moving forward with greater intention.**`
            )}
          />
        </Section>
      </section>

      <Section eyebrow={t("section3_eyebrow", "What you'll experience")} title={t("section3_title", "A weekly space to reconnect and move forward.")} testId="wqe-what">
        <BodyText
          className=""
          value={t.body(
            "section3_body",
            `Each Weekly Quantum Edge session is designed to create space for something that's often difficult to find in everyday life.

Together we'll explore practical ideas, meaningful questions, and perspectives that help you reconnect with yourself and the direction you want your life to take.

Some weeks you'll leave with a new insight. Other weeks you'll simply feel more grounded, more focused, and better prepared for whatever lies ahead.

**Over time, those moments of intentional reconnection begin creating something larger than a single conversation. They become a way of living.**`
          )}
        />
      </Section>

      <section className="bg-brand-paper/60 border-y border-brand-rule/60">
        <Section
          eyebrow={t("section4_eyebrow", "Is Weekly Quantum Edge right for you?")}
          title={t("section4_title", "Weekly Quantum Edge may be right for you if…")}
          testId="wqe-fit"
        >
          <BodyText
            className=""
            value={t.body(
              "section4_body",
              `It may be a good fit if you're someone who values continued growth rather than occasional inspiration.

You enjoy exploring meaningful ideas and applying them to everyday life.

You appreciate thoughtful conversations, practical insights, and the opportunity to pause before life becomes too busy again.

You don't expect every week to change your life. But you understand that consistently reconnecting with yourself can gradually change the way you experience it.

**If that resonates with you, I think you'll feel at home here.**`
            )}
          />
        </Section>
      </section>

      <Section
        eyebrow={t("section5_eyebrow", "Keep growing")}
        title={t("section5_title", "Growth is easier when you don't have to remember it alone.")}
        testId="wqe-keep-growing"
      >
        <BodyText
          className=""
          value={t.body(
            "section5_body",
            `Meaningful growth isn't built through one remarkable day. It's built through many thoughtful ones.

Weekly Quantum Edge offers a simple rhythm of returning — returning to yourself, your intentions, and the life you're intentionally creating.

Whether you've recently discovered Quantum Results or have been walking this path for some time, you'll find a welcoming place to pause, reflect, and continue moving forward.

There will always be more to learn. More to notice. More to become.

You don't have to pursue that journey alone. You'll be joined by thoughtful people who are also choosing to make intentional growth part of everyday life.

**You're welcome here.**`
          )}
        />
        <div className="mt-8 flex flex-wrap gap-4">
          <a
            href="https://fourlevelsexperience.com/weeklyqe"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="wqe-visit-cta"
            className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-6 py-3 text-brand-cream text-[14px] tracking-wide hover:bg-brand-blue transition-colors"
          >
            Visit Weekly Quantum Edge <span aria-hidden>↗</span>
          </a>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="wqe-cta"
            className="inline-flex items-center gap-2 text-[14px] text-brand-ink/70 hover:text-brand-blue transition-colors self-center"
          >
            Or express interest here →
          </button>
        </div>
      </Section>

      <InterestModal
        open={open}
        onClose={() => setOpen(false)}
        source="weekly-edge"
        title="Weekly Quantum Edge"
        lede="Leave your email (and a note, if you like). Dan will reach out with details on the current rhythm and how to join."
        submitLabel="Send"
        afterAction={{
          label: "Continue to Weekly Quantum Edge",
          href: "https://fourlevelsexperience.com/weeklyqe",
        }}
      />

      <NaturalNextSteps
        items={[
          { to: "/daily-quantum-edge", label: "Daily Quantum Edge — a daily practice" },
          { to: "/four-levels", label: "The Four Levels Experience" },
          { to: "/about", label: "About Dan Hoover" },
        ]}
      />
    </div>
  );
}
